"use client"

import { Badge } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { ReconciliationSessionStatus } from "@/features/finance/model/finance-close-trust-types"

/** Badge variant per lifecycle state. Rejected is the only destructive one — it means the work must be redone. */
const STATUS_VARIANT: Record<ReconciliationSessionStatus, string> = {
  [ReconciliationSessionStatus.Draft]: "outline",
  [ReconciliationSessionStatus.PendingReview]: "warning",
  [ReconciliationSessionStatus.Approved]: "success",
  [ReconciliationSessionStatus.Rejected]: "destructive",
}

const STATUS_LABEL_KEY: Record<ReconciliationSessionStatus, string> = {
  [ReconciliationSessionStatus.Draft]: "status.draft",
  [ReconciliationSessionStatus.PendingReview]: "status.pendingReview",
  [ReconciliationSessionStatus.Approved]: "status.approved",
  [ReconciliationSessionStatus.Rejected]: "status.rejected",
}

/**
 * One badge for the reconciliation lifecycle, shared by the list and the session header.
 *
 * Defined once because the list and the detail are the same fact seen twice; two local copies would drift the
 * moment a state is added, and a reviewer comparing a row to the page it opens would see them disagree.
 */
export function ReconciliationStatusBadge({ status }: { status: ReconciliationSessionStatus }) {
  const t = useTranslations("finance.reconciliation")
  return <Badge variant={STATUS_VARIANT[status] as never}>{t(STATUS_LABEL_KEY[status])}</Badge>
}
